import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { useStore } from '../store/useStore';
import { translations } from '../utils/translations';
import { formatDate } from '../utils/dateFormatter';
import type { ChatSession } from '../types/models';

export const ChatHistory: React.FC = () => {
  const { user, currentChatId, loadChat, deleteChat, chatHistory: storeChats, language, clearMessages, startNewChat } = useStore();
  const [chats, setChats] = useState<ChatSession[]>(storeChats);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Update local state when store chatHistory changes
  useEffect(() => {
    console.log('Store chatHistory updated:', {
      count: storeChats.length,
      chats: storeChats.map(c => ({ id: c.id, title: c.title }))
    });
    setChats(storeChats);
  }, [storeChats]);

  // Set up Firebase real-time listener
  useEffect(() => {
    if (!user) {
      console.log('No user found, clearing chats');
      setChats([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    console.log('Setting up chat history listener for user:', user.uid);

    const db = getFirestore();
    const historyRef = collection(db, 'users', user.uid, 'chatHistory');
    const q = query(historyRef, orderBy('createdAt', 'desc'));

    console.log('Subscribing to Firebase collection:', `users/${user.uid}/chatHistory`);
    const unsubscribe = onSnapshot(q,
      async (snapshot) => {
        console.log('Received Firebase update:', {
          totalDocs: snapshot.docs.length,
          empty: snapshot.empty,
          metadata: snapshot.metadata
        });

        if (snapshot.empty) {
          console.log('No chats found in Firebase');
          setChats([]);
          setIsLoading(false);
          return;
        }

        try {
          const firebaseChats = await Promise.all(snapshot.docs.map(async doc => {
            const data = doc.data();
            console.log('Processing Firebase document:', {
              docId: doc.id,
              data: JSON.stringify(data, null, 2)
            });

            if (!data) {
              console.error('Empty document data for doc:', doc.id);
              return null;
            }

            // Get the full chat document to ensure we have all messages
            const chatDoc = await getDoc(doc.ref);
            const fullData = chatDoc.data();

            const chat: ChatSession = {
              id: data.id || doc.id,
              title: data.title || (data.messages?.[0]?.content?.slice(0, 50) + '...') || 'New Chat',
              messages: Array.isArray(fullData?.messages) ? fullData.messages : [],
              createdAt: data.createdAt || data.updatedAt || Date.now(),
              modelId: data.modelId,
              context: data.context || { userInfo: {}, systemPrompt: '' }
            };

            return chat;
          }));

          // Filter out null values and sort
          const validChats = firebaseChats
            .filter((chat): chat is ChatSession => chat !== null)
            .sort((a, b) => b.createdAt - a.createdAt);

          console.log('Processed chat history:', {
            totalChats: validChats.length,
            chats: validChats.map(chat => ({
              id: chat.id,
              title: chat.title,
              messagesCount: chat.messages.length
            }))
          });

          setChats(validChats);
          setIsLoading(false);
          setError(null);
        } catch (error) {
          console.error('Error processing chat history:', error);
          setError('Failed to process chat history');
          setIsLoading(false);
        }
      },
      (err) => {
        console.error('Firebase error:', err);
        setError('Failed to load chat history');
        setIsLoading(false);
      }
    );

    return () => {
      console.log('Cleaning up Firebase listener');
      unsubscribe();
    };
  }, [user]);

  const handleChatClick = async (chatId: string) => {
    try {
      console.log('Loading chat:', chatId);
      const chat = chats.find(c => c.id === chatId);
      if (!chat) {
        console.error('Chat not found in local state:', chatId);
        return;
      }

      // Get fresh data from Firebase
      const db = getFirestore();
      const chatRef = doc(db, 'users', user!.uid, 'chatHistory', chatId);
      const chatDoc = await getDoc(chatRef);
      
      if (!chatDoc.exists()) {
        console.error('Chat document not found in Firebase:', chatId);
        return;
      }

      const data = chatDoc.data();
      console.log('Fresh chat data:', {
        id: chatId,
        messages: data.messages?.length || 0
      });

      // Update the chat in local state with fresh data
      const updatedChat = {
        ...chat,
        messages: Array.isArray(data.messages) ? data.messages : []
      };
  
      setChats(current =>
        current.map(c => c.id === chatId ? updatedChat : c)
      );
      
      // Load the chat in the store with fresh data
      const { messages, context } = updatedChat;
      await loadChat(chatId, messages, context);
    } catch (error) {
      console.error('Error loading chat:', error);
      setError('Failed to load chat');
    }
  };

  if (!user) {
    return (
      <div className="p-4 text-sm text-gray-400">
        Please log in to view chat history
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500 text-sm">
        {error}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-md overflow-hidden">
      {chats.length > 0 ? (
        <div>
          {chats.map((chat) => (
            <div
              key={chat.id}
              className={`group relative transition-all duration-200 ${
                chat.id === currentChatId 
                  ? 'bg-gray-100 dark:bg-gray-700/40 border-l-4 border-gray-800 dark:border-gray-500' 
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 border-l-4 border-transparent'
              }`}
            >
              <div 
                className="p-3 cursor-pointer flex items-center" 
                onClick={() => handleChatClick(chat.id)}
              >
                <div className="flex-1 min-w-0 pr-2">
                  <div className={`font-medium text-sm truncate ${
                    chat.id === currentChatId ? 'text-gray-900 dark:text-white' : 'text-gray-800 dark:text-gray-200'
                  }`}>
                    {chat.title && chat.title !== 'New Chat' 
                      ? chat.title 
                      : (chat.messages?.[0]?.content?.slice?.(0, 50) + '...' || 'Untitled Chat')}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center">
                    <span className="inline-block w-2 h-2 rounded-full mr-1.5 bg-gray-300 dark:bg-gray-600"></span>
                    {formatDate(chat.createdAt)}
                  </div>
                </div>
                
                <button
                  className="shrink-0 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 
                    hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteChat(chat.id);
                  }}
                  title="Delete chat"
                  aria-label="Delete chat"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-6 px-4 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 mb-3">
            <svg className="w-6 h-6 text-gray-400 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {translations[language].noConversationsYet}
          </p>
        </div>
      )}
    </div>
  );
};